import React, { useEffect, useRef, useState } from 'react';
import { Clock, MapPin, Trophy, Users } from 'lucide-react';
import { JugarSection } from '../components/jugar-section';
import { ClubSection } from '../components/club-section';
import { EntrenarSection } from '../components/entrenar-section';
import { SiteFooter } from '../components/site-footer';

const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.5-.515 2.793-1.39.13-.43.13-.802.058-1.146-.144-.487-3.184-1.832-3.385-1.832zM16 .5a15.06 15.06 0 0 0-15.063 15.064c0 2.522.616 5.018 1.794 7.215L.93 30.5l7.93-1.81a14.987 14.987 0 0 0 7.144 1.81C24.288 30.5 31 23.794 31 15.498A14.96 14.96 0 0 0 26.595 4.9 14.97 14.97 0 0 0 16 .5zm0 27.348a12.299 12.299 0 0 1-6.273-1.71l-.444-.273-4.66 1.064 1.087-4.476-.286-.46A12.398 12.398 0 0 1 3.624 15.6 12.39 12.39 0 0 1 16 3.235a12.4 12.4 0 0 1 12.376 12.388A12.41 12.41 0 0 1 16 27.848z" />
  </svg>
);

export const LandingPage = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [showHeader, setShowHeader] = useState(false);
  const logoRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (logoRef.current) {
        const rect = logoRef.current.getBoundingClientRect();
        setShowHeader(rect.bottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Animated Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200 transition-transform duration-500"
        style={{
          background: 'rgb(234, 88, 12)',
          backdropFilter: 'blur(4px)',
          transform: showHeader ? 'translateY(0)' : 'translateY(-100%)',
        }}
      >
        <div className="container flex items-center justify-between h-16 gap-4">
          <button onClick={scrollToTop} className="flex items-center">
            <img
              src="/img/logo-header.png"
              alt="Jugar Hoy Logo"
              width={139}
              height={61}
            />
          </button>
        </div>
      </header>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5491140967673?text=Hola%20JugarHoy%2C%20necesito%20ayuda%20con..."
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactanos por WhatsApp"
        className="fixed right-5 bottom-5 sm:right-6 sm:bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl ring-1 ring-black/5 hover:bg-[#1ebe5d] hover:scale-105 transition-all"
      >
        <WhatsAppIcon size={28} />
      </a>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-orange-600 via-orange-500 to-amber-400 text-white">
          {/* Gloss overlay top */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.18),_transparent_55%)]" />
          {/* Depth overlay bottom */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(180,50,0,0.25),_transparent_65%)]" />
          <div className="container relative z-10 py-6 sm:py-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex flex-col items-center gap-3">
                <img
                  src="/img/logo-full.png"
                  alt="Jugar Hoy Logo Full"
                  className="mx-auto mt-2 w-56 sm:w-48 md:w-72 drop-shadow-xl"
                  ref={logoRef}
                />
                <span className="mb-4 text-white font-bold text-lg sm:text-xl md:text-2xl font-display tracking-wide drop-shadow-md">
                  Conéctate y desafíate en un solo lugar
                </span>
              </div>
            </div>
          </div>
        </section>

        <JugarSection />
        <EntrenarSection />
        <ClubSection />

        {/* Benefits Section */}
        <section className="bg-gradient-to-b from-white to-orange-50 py-8 md:py-16 px-4">
          <div className="container">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
              <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-md border border-orange-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-1 font-bold text-base">Rápido</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Encuentra partidos en minutos</p>
              </div>
              <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-md border border-orange-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-1 font-bold text-base">Eficiente</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Optimiza tu tiempo y recursos</p>
              </div>
              <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-md border border-orange-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-1 font-bold text-base">Inteligente</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Sistema de IA personalizado</p>
              </div>
              <div className="text-center p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-md border border-orange-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-md">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-1 font-bold text-base">Cercano</h3>
                <p className="text-xs sm:text-sm text-muted-foreground">Encuentra deportes en tu zona</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-600 via-orange-700 to-red-800 py-8 md:py-16 text-white px-4">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(255,255,255,0.08),_transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(0,0,0,0.15),_transparent_50%)]" />
          <div className="container text-center relative z-10">
            <h2 className="mb-3 text-xl sm:text-2xl md:text-4xl font-bold font-display drop-shadow">
              ¡No esperes más! El deporte te está esperando
            </h2>
            <p className="mb-6 text-sm sm:text-base text-orange-100">
              Únete a nuestra comunidad y comienza a disfrutar de todos los beneficios
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};
